import { createSelector } from 'reselect';
import _ from 'lodash';
import { selectedTeamSelector } from './ui-state';
import { getSensorAuthorizations } from './sensors-authorizations';
import {
  teamsByIdSelector,
  teamsFlatTreeSelector
} from 'state/selectors/teams';
import { getTreeTeamsContributors } from 'common/utils/contributors.utils';
import { filterAllContributors } from 'views/root/views/home/views/settings/views/ContributorsAndTeams/ContributorsAndTeams.service';

const getCurrentTeams = state => state.currentTeams;

const rawContributorsSelector = state => state.contributors || [];

export const contributorsSelector = createSelector(
  rawContributorsSelector,
  contributors => contributors.filter(({ parent_id }) => !parent_id)
);

export const contributorsByIdSelector = createSelector(
  [contributorsSelector],
  contributors => _.keyBy(contributors, 'id')
);

export const individualContributorsSelector = createSelector(
  [contributorsSelector],
  contributors => contributors.filter(({ type }) => type !== 'group')
);

export const selectedTeamContributorsSelector = createSelector(
  [individualContributorsSelector, selectedTeamSelector, getCurrentTeams],
  (contributors, selectedTeam, currentTeams) => {
    if (!selectedTeam) {
      return contributors;
    }
    const currentTeam = currentTeams.find(({ id }) => id === selectedTeam.id);
    if (!currentTeam || !currentTeam.contributor_ids) {
      return [];
    }
    return contributors.filter(({ id }) =>
      currentTeam.contributor_ids.includes(id)
    );
  }
);

export const teamContributorsSelector = createSelector(
  [individualContributorsSelector, teamsByIdSelector, (_, teamId) => teamId],
  (contributors, teamsById, teamId) => {
    if (!teamId) {
      return contributors;
    }

    const team = teamsById[teamId];
    if (!team || !team.contributor_ids) {
      return [];
    }

    return contributors.filter(({ id }) => team.contributor_ids.includes(id));
  }
);

export const rawContributorsWithSensorAuthSelector = createSelector(
  [getSensorAuthorizations, rawContributorsSelector],
  (sensors, contributors) => {
    const sensorsMap = _.keyBy(sensors, 'id');
    return contributors.map(contributor => {
      const { sensor_authorization_id } = contributor;
      return { ...contributor, sensor: sensorsMap[sensor_authorization_id] };
    });
  }
);

export const selectedTeamFlatContributorsSelector = createSelector(
  [teamsFlatTreeSelector, contributorsSelector, selectedTeamSelector],
  (teams, contributors, selectedTeam) => {
    const selectedTeamId = _.get(selectedTeam, 'id');

    if (!selectedTeamId) {
      return contributors;
    }

    const selectedTeamObj = teams.find(({ id }) => id === selectedTeamId);

    return selectedTeamObj
      ? getTreeTeamsContributors({
          teams: [selectedTeamObj],
          contributors
        })
      : [];
  }
);

export const contributorsWithTeamIdsSelector = createSelector(
  [contributorsSelector, getCurrentTeams],
  (contributors, currentTeams) => {
    const contributorsTeamIds = filterAllContributors(currentTeams).reduce(
      (acc, team) => {
        const { id, contributor_ids: contributorIds } = team;
        contributorIds.forEach(contributorId => {
          if (!acc.hasOwnProperty(contributorId)) {
            acc[contributorId] = new Set();
          }

          acc[contributorId].add(id);
        });
        return acc;
      },
      {}
    );

    return contributors.map(contributor => ({
      ...contributor,
      teamIds: Array.from(contributorsTeamIds[contributor.id] || [])
    }));
  }
);
